import moment from 'moment'
import config from '@/config'
import { assign } from 'lodash'

import { ZERO_TIMEOBJECT, TIMESTRING_NULL_VALUE } from '@/modules/timetracking/constants'

/**
 * Removes all prepadded zeros from the given TimeString slice. E.g.: "08" will become "8".
 *
 * @private
 * @param {String} timeStringSlice
 * @return {String}
 */
const removePrepaddedZeros = (timeStringSlice) => {
  // if only 0's are present return '0'
  // else remove all 0's
  return timeStringSlice.match(/^0+$/g, '') ? '0' : timeStringSlice.replace(/^0*/g, '')
}

/**
 * @private
 * @param {String} numberAsString
 * @return {Number}
 */
const isNegativeOrNotANumber = (numberAsString) => {
  const sanitizedNumberAsString = removePrepaddedZeros(numberAsString)
  const num = parseInt((sanitizedNumberAsString || 0), 10)
  // check if the result is NaN through recalculating and comparing both results, since NaN !== NaN
  return num < 0 || num !== parseInt(numberAsString, 10)
}

/**
 * @public
 * @param {String} timeString
 * @return {String}
 */
export const sanitizeTimeString = (timeString) => {
  const timeSlices = (timeString) ? timeString.split(':') : []
  const sanitizedTimeSlices = timeSlices.map(removePrepaddedZeros)

  if (sanitizedTimeSlices.length === 1 && !isNegativeOrNotANumber(sanitizedTimeSlices[0])) {
    return `${sanitizedTimeSlices[0]}:00`
  } else if (sanitizedTimeSlices.length > 1
    && !isNegativeOrNotANumber(sanitizedTimeSlices[0])
    && !isNegativeOrNotANumber(sanitizedTimeSlices[1])) {
    // prepad a zero if minutes are < 10
    const minutes = (sanitizedTimeSlices[1].length === 2) ? sanitizedTimeSlices[1] : `0${sanitizedTimeSlices[1]}`
    return `${sanitizedTimeSlices[0]}:${minutes}`
  }
  return '0:00'
}

/**
 * Takes a TimeString like e.g. "8:15" and converts it into a TimeObject like e.g. {hour:8,minute:15}
 *
 * @public
 * @param {String} timeString
 * @return {{hour:Number,minute:Number}}
 */
export const convertStringToTimeObject = (timeString) => {
  const validTimeString = sanitizeTimeString(timeString)
  const timeSlices = validTimeString.split(':')
  const timeObject = {
    hour: parseInt(timeSlices[0], 10),
    minute: parseInt(timeSlices[1], 10),
  }

  return timeObject
}

/**
 * Checks whether the given TimeObject's values are within the 24h daytime boundaries or not:
 * Valid hours: 0 - 23
 * Valid Minutes: 0 - 59
 *
 * @public
 * @param {{hour:number,minute:number}} timeObject
 * @return {boolean}
 */
export const isValid24HourTimeObject = (timeObject) => {
  const isWithinHourBoundary = (timeObject.hour >= 0 && timeObject.hour < 24)
  const isWithinMinuteBoundary = (timeObject.minute >= 0 && timeObject.minute < 60)
  return isWithinHourBoundary && isWithinMinuteBoundary
}

/**
 * Calculates the time difference between the given start and end time. Returns zero values if start time > end time.
 *
 * @public
 * @param {String} startTimeString
 * @param {String} endTimeString
 * @return {{hour:Number,minute:Number}}
 */
export const calculateDiffBetweenTwoTimeStrings = (startTimeString, endTimeString) => {
  const timeStartObject = convertStringToTimeObject(startTimeString)
  const timeEndObject = convertStringToTimeObject(endTimeString)
  const calculatedTrackedTime = {
    hour: 0,
    minute: 0,
  }

  // only calculate the difference if start < end; else return { hour: 0, minute: 0 }
  if ((timeStartObject.hour < timeEndObject.hour)
    || (timeStartObject.hour === timeEndObject.hour && timeStartObject.minute < timeEndObject.minute)) {
    if (timeStartObject.minute <= timeEndObject.minute) {
      calculatedTrackedTime.minute = timeEndObject.minute - timeStartObject.minute
    } else {
      calculatedTrackedTime.minute = 60 - (timeStartObject.minute - timeEndObject.minute)
      calculatedTrackedTime.hour = -1
    }
    calculatedTrackedTime.hour = calculatedTrackedTime.hour + (timeEndObject.hour - timeStartObject.hour)
  }

  return calculatedTrackedTime
}

/**
 * Builds a 'H:mm' formatted time string with the given duration in minutes.
 *
 * @public
 * @param {Number} durationInMinutes
 * @return {String}
 */
export const convertMinutesToTimeString = (durationInMinutes) => {
  let timeString = '0:00'
  if (durationInMinutes > 0) {
    const hours = Math.floor(durationInMinutes / 60)
    let minutes = durationInMinutes % 60
    if (minutes === 0) {
      minutes = '00'
    } else if (minutes < 10) {
      minutes = `0${minutes}`
    }
    timeString = `${hours}:${minutes}`
  }

  return timeString
}

/**
 * Returns a given time string as minutes for comparison
 *
 * Allowed: 01:00, 1:00, 1, 111111
 * @public
 * @param {Text} text
 * @return {Number}
 */
export const formatTimeStringAsMinutes = (text) => {
  const split = text.split(':')
  const hours = parseInt(split[0] * 60, 10)
  let minutes = split[1]

  if (!minutes) { minutes = 0 } else {
    if (minutes.length > 2) {
      minutes = parseInt(minutes.slice(0, 2))
    }
    if (minutes.length === 1) {
      minutes = parseInt(minutes, 10) * 10
    } else {
      minutes = parseInt(minutes, 10)
    }
  }

  return hours + minutes
}

/**
 * Sanitizes 'null' dates given by the XData server.
 *
 * @public
 * @param {Date} dateTime
 * @return {String}
 */

export const convertDatetimeToTimeString = (dateTime) => {
  if (!dateTime) {
    return TIMESTRING_NULL_VALUE
  }
  let sanitizedDatetime = moment(dateTime)
  if (sanitizedDatetime === moment(config.datetimeEpochValue)) {
    // if an epoch data (-> date null value) is given, set Date.now() as default
    sanitizedDatetime = moment(ZERO_TIMEOBJECT)
  }

  return sanitizedDatetime.format('HH:mm')
}

/**
 * Returns duration between two dates
 *
 * @public
 * @param {String} startDate
 * @param {String} endDate
 * @return {Number}
 */

export const calculateDuration = (startDate, endDate) => {
  const calculatedTimeObject = calculateDiffBetweenTwoTimeStrings(convertDatetimeToTimeString(startDate), convertDatetimeToTimeString(endDate))
  return (calculatedTimeObject.hour * 60) + calculatedTimeObject.minute
}

/**
 * Converts a given Date (either native or moment.js date) into a TimeObject instance.
 *
 * @public
 * @param {Date} dateTime
 * @return {{hour:Number,minute:Number}}
 */
export const convertDatetimeToTimeObject = (dateTime) => {
  const date = moment(dateTime, config.datetimeFormat.api)
  return {
    hour: date.hour(),
    minute: date.minute(),
  }
}

/**
 * Sanitizes 'null' dates given by the XData server.
 *
 * @param {Number} durationInMinutes
 * @param {Date} startDate
 * @param {Date} endDate
 * @return {String}
 */
export const getTrackedTimeString = (durationInMinutes, startDate, endDate) => {
  if (durationInMinutes > 0) {
    return convertMinutesToTimeString(durationInMinutes)
  } else {
    const timeStartObject = convertDatetimeToTimeObject(startDate)
    const timeEndObject = convertDatetimeToTimeObject(endDate)
    const calculatedTrackedTime = assign({}, ZERO_TIMEOBJECT)

    if (
      timeStartObject.hour < timeEndObject.hour
      || (timeStartObject.hour === timeEndObject.hour && timeStartObject.minute < timeEndObject.minute)
    ) {
      if (timeStartObject.minute <= timeEndObject.minute) {
        calculatedTrackedTime.minute = timeEndObject.minute - timeStartObject.minute
      } else {
        calculatedTrackedTime.minute = 60 - timeStartObject.minute - timeEndObject.minute
        calculatedTrackedTime.hour = -1
      }
      calculatedTrackedTime.hour = calculatedTrackedTime.hour + (timeEndObject.hour - timeStartObject.hour)
    }

    return moment(calculatedTrackedTime).format('H:mm')
  }
}
